"use client";

import { useEffect, useState, type CSSProperties } from "react";
import { usePostHog } from "posthog-js/react";
import { Download } from "@/components/icons";
import { cn } from "@/lib/utils";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui";
import { downloadPng, downloadSvg } from "./export";
import { LogoSvg } from "./logo-svg";
import { getRenderModel } from "./svg-source";
import { NumberField, SegmentedControl, SizeField, ToggleRow } from "./parts";
import type { BuilderConfig, BuilderPreset } from "./types";

const WORDMARK_PRESETS = [200, 400, 800, 1200, 1600, 2400];
const SQUARE_PRESETS = [64, 128, 256, 512, 1024];
const SYMBOL_PRESETS = [16, 32, 48, 64, 128, 256, 512];

const DEFAULTS: BuilderConfig = {
  asset: "wordmark",
  color: "white",
  background: "none",
  registered: true,
  padding: false,
  canvas: "natural",
  size: 800,
  customWidth: 1200,
  customHeight: 630,
  cornerRadius: 0,
};

const CHECKERBOARD: CSSProperties = {
  backgroundImage: "repeating-conic-gradient(#1B1B1E 0% 25%, #101012 0% 50%)",
  backgroundSize: "16px 16px",
};

function resolveConfig(preset: BuilderPreset): BuilderConfig {
  const merged: BuilderConfig = { ...DEFAULTS, ...preset };
  if (merged.background === merged.color) merged.background = "none";
  if (preset.size === undefined && merged.asset === "symbol") merged.size = 512;
  return merged;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preset?: BuilderPreset;
}

export default function LogoBuilderDialog({ open, onOpenChange, preset }: Props) {
  const posthog = usePostHog();
  const [config, setConfig] = useState<BuilderConfig>(() => resolveConfig(preset ?? {}));
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    if (open) setConfig(resolveConfig(preset ?? {}));
  }, [open, preset]);

  const update = (patch: Partial<BuilderConfig>) => {
    setConfig((prev) => {
      const next = { ...prev, ...patch };
      if (next.background === next.color) next.background = "none";
      if (patch.asset && patch.asset !== prev.asset) {
        next.size = patch.asset === "symbol" ? 512 : DEFAULTS.size;
      }
      return next;
    });
  };

  const { geometry } = getRenderModel(config);
  const isWordmark = config.asset === "wordmark";
  const isCustom = isWordmark && config.canvas === "custom";

  const capture = (format: "svg" | "png") => {
    posthog.capture("brand_asset_downloaded", { ...config, format });
  };

  const handleSvg = () => {
    downloadSvg(config);
    capture("svg");
  };

  const handlePng = async () => {
    setRendering(true);
    try {
      await downloadPng(config);
      capture("png");
    } catch (error) {
      console.error("PNG export failed", error);
    } finally {
      setRendering(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 p-0">
        <DialogHeader className="border-border border-b px-5 py-3">
          <DialogTitle>Logo builder</DialogTitle>
          <DialogDescription>
            Compose the exact asset you need — color, background, clear space, and size.
          </DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 shrink gap-5 overflow-y-auto p-5 md:basis-[470px] md:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0">
            <div
              className="border-border flex h-52 items-center justify-center overflow-hidden rounded-xl border p-4"
              style={CHECKERBOARD}
            >
              {/* Sized to the exact export aspect ratio — the file is precisely this box. */}
              <div
                className={cn(
                  "max-w-full",
                  config.background === "none" && "outline-1 outline-white/15 outline-dashed"
                )}
                style={{
                  aspectRatio: `${geometry.outputW} / ${geometry.outputH}`,
                  width: `min(100%, ${(176 * geometry.outputW) / geometry.outputH}px)`,
                }}
              >
                <LogoSvg config={config} showGuides={config.padding} className="h-full w-full" />
              </div>
            </div>
            <div className="text-muted-foreground mt-2 flex items-center justify-between gap-3 text-[11px]">
              <span>
                {config.padding
                  ? "Clear space equals one “o” from the wordmark — shown as the ghosted glyphs."
                  : "Exports exactly what you see."}
              </span>
              <span className="shrink-0 font-mono tabular-nums">
                {geometry.outputW} × {geometry.outputH} px
              </span>
            </div>

            <div className="mt-4">
              {!isWordmark && (
                <SizeField
                  label="Size"
                  value={config.size}
                  presets={SYMBOL_PRESETS}
                  onChange={(size) => update({ size })}
                />
              )}
              {isWordmark && config.canvas === "natural" && (
                <SizeField
                  label="Width"
                  value={config.size}
                  presets={WORDMARK_PRESETS}
                  onChange={(size) => update({ size })}
                />
              )}
              {isWordmark && config.canvas === "square" && (
                <SizeField
                  label="Size"
                  value={config.size}
                  presets={SQUARE_PRESETS}
                  onChange={(size) => update({ size })}
                />
              )}
              {isCustom && (
                <div>
                  <p className="text-muted-foreground mb-1.5 text-[10px] font-medium tracking-[0.18em] uppercase">
                    Canvas size
                  </p>
                  <div className="flex items-center gap-2">
                    <NumberField
                      label="W"
                      value={config.customWidth}
                      onCommit={(customWidth) => update({ customWidth })}
                    />
                    <NumberField
                      label="H"
                      value={config.customHeight}
                      onCommit={(customHeight) => update({ customHeight })}
                    />
                  </div>
                  <p className="text-muted-foreground mt-1.5 text-[11px] leading-relaxed">
                    The logo is centered inside the canvas.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <SegmentedControl
                label="Asset"
                options={[
                  { value: "wordmark", label: "Logo" },
                  { value: "symbol", label: "Favicon" },
                ]}
                value={config.asset}
                onChange={(asset) => update({ asset })}
              />
              <SegmentedControl
                label="Color"
                options={[
                  { value: "white", label: "White" },
                  { value: "black", label: "Black" },
                ]}
                value={config.color}
                onChange={(color) => update({ color })}
              />
            </div>
            <SegmentedControl
              label="Background"
              options={[
                { value: "none", label: "None" },
                { value: "black", label: "Black", disabled: config.color === "black" },
                { value: "white", label: "White", disabled: config.color === "white" },
              ]}
              value={config.background}
              onChange={(bg) => update({ background: bg })}
            />
            {isWordmark && (
              <SegmentedControl
                label="Canvas"
                options={[
                  { value: "natural", label: "Natural" },
                  { value: "square", label: "Square" },
                  { value: "custom", label: "Custom" },
                ]}
                value={config.canvas}
                onChange={(canvas) => update({ canvas })}
              />
            )}
            {config.background !== "none" && (
              <div>
                <p className="text-muted-foreground mb-1.5 text-[10px] font-medium tracking-[0.18em] uppercase">
                  Corners
                </p>
                <NumberField
                  label="Radius"
                  value={config.cornerRadius}
                  onCommit={(cornerRadius) => update({ cornerRadius })}
                  min={0}
                  max={2048}
                />
              </div>
            )}
            {isWordmark && (
              <ToggleRow
                label="® mark"
                checked={config.registered}
                onChange={(registered) => update({ registered })}
              />
            )}
            <ToggleRow
              label="Padding"
              checked={config.padding}
              onChange={(padding) => update({ padding })}
            />
          </div>
        </div>

        <DialogFooter className="border-border flex-row justify-end border-t px-5 py-3">
          <Button variant="outline" size="sm" onClick={handleSvg}>
            Download SVG
          </Button>
          <Button size="sm" onClick={handlePng} disabled={rendering}>
            <Download />
            {rendering ? "Rendering…" : "Download PNG"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
